/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import React, { useMemo } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { Preview } from './Preview.tsx';
import {
	getBodyForTemplate,
	getTemplateIdsToFetch,
	getTemplateId,
	type TemplatePreviewTemplate,
} from './templatePreviewHelpers.tsx';
import { getDataProviders } from './PreviewRenderer/adfHelpers.ts';
import type {
	TemplatePreviewTemplateBodiesQuery as TemplatePreviewTemplateBodiesQuery$data,
	TemplatePreviewTemplateBodiesQueryVariables as TemplatePreviewTemplateBodiesQuery$variables,
} from './__types__/TemplatePreviewTemplateBodiesQuery.ts';

const DEFAULT_NUM_PREVIEWS_TO_FETCH_AT_ONCE = 30;
const PREVIEW_MIN_HEIGHT = 500;
const DEFAULT_MARGIN_BOTTOM = 30;

type Props = {
	spaceKey: string;
	templates: TemplatePreviewTemplate[];
	currentTemplateIndex?: number;
	numPreviewsToFetchAtOnce?: number;
	isScrollable?: boolean;
	marginBottom?: number;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div<{ isScrollable: boolean }>({
	// See <TemplateContainer> note on background paradox
	background: token('elevation.surface.overlay'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props) => (props.isScrollable ? '100%' : `${PREVIEW_MIN_HEIGHT}px`),
});

export const TemplatePreview = ({
	spaceKey,
	templates,
	currentTemplateIndex = -1,
	numPreviewsToFetchAtOnce = DEFAULT_NUM_PREVIEWS_TO_FETCH_AT_ONCE,
	isScrollable = false,
	marginBottom = DEFAULT_MARGIN_BOTTOM,
}: Props) => {
	const { cloudId, userId } = useSessionData();

	const ids = useMemo(
		() => getTemplateIdsToFetch(templates, currentTemplateIndex, numPreviewsToFetchAtOnce),
		[templates, currentTemplateIndex, numPreviewsToFetchAtOnce],
	);
	const { data, loading, error } = useQuery<
		TemplatePreviewTemplateBodiesQuery$data,
		TemplatePreviewTemplateBodiesQuery$variables
	>(
		gql`
			query TemplatePreviewTemplateBodiesQuery($spaceKey: String!, $ids: [String!]!) {
				templateBodies(spaceKey: $spaceKey, ids: $ids) {
					nodes {
						id
						body {
							value
							representation
						}
					}
				}
			}
		`,
		{
			variables: { spaceKey, ids },
			skip: !spaceKey || !templates.length || !ids.length,
		},
	);

	const currentTemplate = templates[currentTemplateIndex];

	const currentTemplateId = currentTemplate ? getTemplateId(currentTemplate) : '';
	const fabricDataProviders = useMemo(
		() =>
			getDataProviders({
				cloudId,
				userId,
				templateIds: ids,
				spaceKey,
				currentTemplateId,
			}),
		[cloudId, userId, ids, spaceKey, currentTemplateId],
	);
	return (
		<Wrapper isScrollable={isScrollable}>
			<Preview
				templateBody={getBodyForTemplate(data, currentTemplate)}
				isLoading={loading}
				error={error}
				template={currentTemplate}
				isScrollable={isScrollable}
				marginBottom={marginBottom}
				fabricDataProviders={fabricDataProviders}
			/>
		</Wrapper>
	);
};
